/*
 * CD customizations (painting-to-gogh):
 *   - #handleVariantUpdate no longer calls `replaceWith` unconditionally.
 *     It first compares the current and new gallery's first-image src
 *     (Shopify CDN path before the ?) and bails out when they match —
 *     PtG products often share a featured_image across Studio /
 *     Essentials / Refill variants, so the legacy unconditional swap
 *     was flashing the gallery on every option click. When the image
 *     genuinely differs, morph (not replaceWith) keeps the existing
 *     <img> nodes in the DOM and updates only what actually changed.
 *   - Added morph import + #firstImageSrc helper.
 * On Horizon upstream sync: re-merge the upstream #handleVariantUpdate
 * carefully — the equality check + morph swap should stay.
 */
import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    // Perceived-perf: skip the swap entirely when the new gallery's first
    // image is identical to the current first image. Stock Horizon called
    // `replaceWith` unconditionally — every variant change tore the gallery
    // out of the DOM, the new <img> started fetching/decoding from scratch,
    // and customers saw a blank flash even when picking a variant that
    // shared its featured_image with the previous one (common on PtG
    // products where Studio/Essentials/Refill all share one painting).
    //
    // When the image DOES differ, prefer `morph` over `replaceWith`. Morph
    // diffs by attribute and only updates what actually changed, so the
    // existing <img> with the matching `src` stays in the DOM (no decode
    // restart on identical entries within multi-image galleries) and only
    // the slides whose URLs differ get new <img>s. Net: no flicker on
    // same-image swaps; minimum-cost flicker on real swaps.
    const currentSrc = this.#firstImageSrc(this);
    const newSrc = this.#firstImageSrc(newMediaGallery);
    if (currentSrc && newSrc && currentSrc === newSrc) {
      return;
    }

    morph(this, newMediaGallery);
  };

  /**
   * Reads the src of the first <img> inside a media-gallery, normalized
   * by stripping the Shopify CDN width/height/crop query so two URLs
   * that point at the same source asset compare equal even if the
   * server-rendered srcset chose a different default width.
   *
   * @param {Element} galleryEl - The media-gallery element.
   * @returns {string} The normalized src, or '' if none.
   */
  #firstImageSrc(galleryEl) {
    const img = galleryEl.querySelector('img');
    if (!img) return '';
    const raw = img.getAttribute('src') || img.currentSrc || '';
    if (!raw) return '';
    // Drop query string — Shopify CDN URLs encode width/height/crop there.
    // What identifies the "same image" is the path before the ?.
    const qIndex = raw.indexOf('?');
    return qIndex === -1 ? raw : raw.slice(0, qIndex);
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
